import React, { useState, useCallback, Suspense } from 'react';
import SpeedLoader    from './components/SpeedLoader';
import LaptopLanding  from './components/LaptopLanding'; // NEW — Floating Workstation Landing
import Desktop        from './components/Desktop';
import Taskbar        from './components/Taskbar';
import SystemCore     from './components/SystemCore';    // Fedora OS Portfolio Dashboard
import Background     from './components/Background';
import Cursor         from './components/Cursor';

import CreativeEntry from './creative/routes/CreativeEntry';

import './App.css';

const WorkshopEntry = React.lazy(() => import('./workshop/routes/WorkshopEntry'));

export default function App() {
  // Route shortcuts with hash support for static environments like GitHub Pages
  const [currentPath, setCurrentPath] = useState(() => window.location.pathname);
  const [currentHash, setCurrentHash] = useState(() => window.location.hash);

  React.useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
      setCurrentHash(window.location.hash);
    };
    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('hashchange', handleLocationChange);
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('hashchange', handleLocationChange);
    };
  }, []);

  const isCreativeMode = currentPath === '/creative' || currentPath === '/studio' || currentHash === '#/creative' || currentHash === '#/studio';
  const isWorkshopMode = currentPath === '/workshop' || currentHash === '#/workshop';

  // First-visit boot screen
  const [booted, setBooted] = useState(() => {
    try { return localStorage.getItem('deepithos_visited') === 'true'; }
    catch { return false; }
  });

  // Theme state: always opens in light mode
  const [theme, setTheme] = useState('light');

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Three-state view:
  //   'landing'  → LaptopLanding (the new floating workstation)
  //   'fedora'   → SystemCore   (portfolio dashboard — Projects / Skills / etc.)
  //   'desktop'  → Desktop + Taskbar  (Hyprland OS shell)
  const [view, setView] = useState('landing');

  // Hyprland workspace state (only relevant in 'desktop' view)
  const [activeWorkspace,   setActiveWorkspace]   = useState(1);
  const [activeWindowTitle, setActiveWindowTitle] = useState('hyprland - terminal');

  const handleBootComplete = useCallback(() => {
    try { localStorage.setItem('deepithos_visited', 'true'); } catch {}
    setBooted(true);
  }, []);

  // ─── Special routes ───────────────────────────────────
  if (isCreativeMode) return <CreativeEntry />;

  if (isWorkshopMode) {
    return (
      <Suspense fallback={<div style={{ background: '#0d0c0a', height: '100vh' }} />}>
        <WorkshopEntry />
      </Suspense>
    );
  }

  // ─── Main app ─────────────────────────────────────────
  return (
    <>
      {/* Boot sequence — shows only on first ever visit */}
      {!booted && <SpeedLoader onComplete={handleBootComplete} />}

      {booted && (
        <>
          {/* Global Particles & Cursor layer for Landing Page */}
          <div className={`landing-particles-container ${view === 'landing' ? 'visible' : 'hidden'}`}>
            <Background />
            <Cursor />
          </div>

          {/* ── Landing: Floating Laptop Workstation ── */}
          {view === 'landing' && (
            <LaptopLanding
              onEnterFedora={() => setView('fedora')}
              onEnterWorkshop={() => { window.location.hash = '#/workshop'; }}
              theme={theme}
              setTheme={setTheme}
            />
          )}

          {/* ── Fedora OS: Full Portfolio Dashboard ── */}
          {view === 'fedora' && (
            <SystemCore
              onExit={() => setView('desktop')}
              onEnter={() => {}}
              theme={theme}
              setTheme={setTheme}
            />
          )}

          {/* ── Hyprland OS Desktop ── */}
          {view === 'desktop' && (
            <>
              <Desktop
                activeWorkspace={activeWorkspace}
                setActiveWorkspace={setActiveWorkspace}
                onFocusChange={setActiveWindowTitle}
                onLaunchRecruiterMode={() => setView('fedora')}
              />
              <Taskbar
                onRecruiterMode={() => setView('fedora')}
                recruiterMode={false}
                activeWorkspace={activeWorkspace}
                setActiveWorkspace={setActiveWorkspace}
                activeWindowTitle={activeWindowTitle}
              />
            </>
          )}
        </>
      )}
    </>
  );
}